import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Grid, Typography } from '@mui/material';
import { Button } from '@baseComponents';
import { Toast } from '@components';
import { getOfficial, OfficialData } from '../../networkRequest/officialsService';
import { useUser } from '../../hooks/useUser';
import CredentialsList from './components/CredentialsList';

import {
  EditUserDialog,
  LawEnforcementIdSection,
  SharePinDialog,
  UserDetailsHeader,
  UserDetailsView,
  UserPhotoPanel,
} from './components';

export const UserDetailsPage: React.FC = () => {
  const { officialId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [official, setOfficial] = useState<OfficialData | null>(null);
  const [updatedPin, setUpdatedPin] = useState<string | null>(null); // pin is stored separately so it can be updated separately from the rest of official data, guaranteeing it is obfuscated accordingly
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [sharePinDialogOpen, setSharePinDialogOpen] = useState(false);

  const refreshData = useCallback(async () => {
    if (user && officialId) {
      try {
        const { data, error } = await getOfficial(user?.accessToken, officialId);
        if (data) {
          setOfficial(data);
        } else if (error) {
          setErrorMessage(error);
        }
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    }
  }, [officialId, user]);

  const onUserUpdated = (updatedUser: OfficialData) => {
    setSuccessMessage(`Successfully updated ${updatedUser.first_name} ${updatedUser.last_name}.`);
    if(updatedUser.pin) {
      setUpdatedPin(updatedUser.pin);
      setSharePinDialogOpen(true);
    }
  }

  useEffect(() => {
    refreshData().finally(() => setLoading(false));
  }, [refreshData]);

  if (loading) {
    return (
      <Grid
        container
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '80%'
        }}><CircularProgress /></Grid>
    );
  }

  if (!official) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80%'
        }}>
        <Typography>This user does not exist.</Typography>
        <Button text='Back to Users' onClick={() => navigate('/users')} />
      </Grid>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Box sx={{ paddingLeft: 3, paddingRight: 3 }}>
        <UserDetailsHeader
          official={official}
          refreshData={refreshData}
          setEditUserDialogOpen={setEditUserDialogOpen}
          setUpdatedPin={setUpdatedPin}
          setSharePinDialogOpen={setSharePinDialogOpen}
          setError={setErrorMessage}
        />
      </Box>
      <Container>
        {/* Extra bottom padding so the page scrolls a little past the content */}
        <Grid
          container
          spacing={4}
          sx={{
            mt: 3,
            mb: 3,
            pb: 8
          }}>
          <Grid size={{ xs: 12, md: 'auto' }}>
            <UserPhotoPanel
              official={official}
              setData={setOfficial}
              setError={setErrorMessage}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 'grow' }} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}>
              <UserDetailsView official={official} />
              {official.employment_type === 'law_enforcement' && (
                <LawEnforcementIdSection
                  officialId={official.id}
                  frontUrl={official.id_card_front_url}
                  backUrl={official.id_card_back_url}
                  setData={setOfficial}
                  refreshData={refreshData}
                  setError={setErrorMessage}
                />
              )}
              <CredentialsList
                officialId={official.id}
                credentials={official.official_credentials}
                refreshData={refreshData}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      <EditUserDialog
        open={editUserDialogOpen}
        setDialogOpen={setEditUserDialogOpen}
        currentOfficial={official}
        onError={setErrorMessage}
        onSuccess={onUserUpdated}
        refreshData={refreshData}
      />
      <SharePinDialog
        open={sharePinDialogOpen}
        setDialogOpen={setSharePinDialogOpen}
        official={official}
        pin={updatedPin ?? official.pin ?? null}
        onError={setErrorMessage}
        onSuccess={setSuccessMessage}
      />
      {errorMessage && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
    </Box>
  );
};

export default UserDetailsPage;
